import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

import { useDiagramStore } from '../../store/useDiagramStore';
import { GraphTypes } from '../../types/general';
import { SelectedItem } from '../../components/SelectionPopup/SelectionPopup';
import LegalStructureSelection from './components/LegalStructureSelection';
import { dispatchAsidePortalContentChanged } from '../../utils/aside-portal-event';

export interface LegalStructuresSelectedData {
    assets: SelectedItem[];
    investors: SelectedItem[];

    selfManaged: boolean;
    noFixedCapital: boolean;
    openEnded: boolean;
    corporate: boolean;
    ifEltif: boolean;
}

export interface LegalStructuresLayoutContextType {
    // Selected data
    selectedData: LegalStructuresSelectedData;
    setSelectedData: React.Dispatch<React.SetStateAction<LegalStructuresSelectedData>>;

    // Menu props
    handleCheckbox: (prop: string) => (event: React.FormEvent<HTMLInputElement>) => Promise<void>;
    active: boolean;
    hidden: boolean;
    onEdit: () => void;
    setOnEdit: (handler: () => void) => void;

    // Popup control
    showPopup: boolean;
    setShowPopup: React.Dispatch<React.SetStateAction<boolean>>;

    // Page state
    pageReady: boolean;
    setPageReady: React.Dispatch<React.SetStateAction<boolean>>;
    tableUrl: string;
    setTableUrl: React.Dispatch<React.SetStateAction<string>>;

    // Route info
    isExpandedView: boolean;

    checkClicked: boolean;
    setCheckClicked: React.Dispatch<React.SetStateAction<boolean>>;
    toggleRequest: boolean;
    setToggleRequest: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LegalStructuresLayoutContext = createContext<LegalStructuresLayoutContextType | null>(null);

const LegalStructuresLayout = () => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const isExpandedView = location.pathname.includes('/expanded');

    const [resetData, setGraphStoreData, getCountries] = useDiagramStore((state) => [
        state.resetData,
        state.setStoreData,
        state.getCountries,
    ]);

    const [selectedData, setSelectedData] = useState<LegalStructuresSelectedData>({
        selfManaged: false,
        noFixedCapital: true,
        openEnded: false,
        corporate: false,
        ifEltif: false,
        assets: [],
        investors: [],
    });

    const [showPopup, setShowPopup] = useState(false);
    const [tableUrl, setTableUrl] = useState('');
    const [pageReady, setPageReady] = useState(false);

    const [checkClicked, setCheckClicked] = useState(false);
    const [toggleRequest, setToggleRequest] = useState(false);

    useEffect(() => {
        if (!isExpandedView && pageReady && !showPopup && searchParams.toString().length) {
            setToggleRequest(true);
        }
    }, [isExpandedView]);

    useEffect(() => {
        if (showPopup) {
            setPageReady(true);
            dispatchAsidePortalContentChanged(false);
        } else {
            // dispatchAsidePortalContentChanged(true);
        }
    }, [showPopup]);

    useEffect(() => {
        if (!searchParams.toString().length) {
            (async () => {
                await initPage();
            })();
        }
    }, [searchParams]);

    useEffect(() => {
        setGraphStoreData({
            useInvestmentStructure: false,
            graphType: GraphTypes.Legal,
        });

        (async () => {
            await initPage();
        })();
    }, []);

    const initPage = async () => {
        await getCountries();

        const investors = searchParams.get('investors');
        const assets = searchParams.get('assets');

        const selfManaged = searchParams.get('selfManaged');
        const noFixedCapital = searchParams.get('noFixedCapital');
        const corporate = searchParams.get('corporate');
        const openEnded = searchParams.get('openEnded');
        const ifEltif = searchParams.get('ifEltif');

        if (!investors || !assets) {
            resetData();
            setShowPopup(true);
            return;
        }

        setSelectedData({
            ...selectedData,
            investors: JSON.parse(investors),
            assets: JSON.parse(assets),
            selfManaged: selfManaged === 'true',
            noFixedCapital: noFixedCapital === 'true',
            corporate: corporate === 'true',
            openEnded: openEnded === 'true',
            ifEltif: ifEltif === 'true',
        });

        setCheckClicked(true);
        setPageReady(true);
    };

    const handleCheckbox = (prop: string) => async (event: React.FormEvent<HTMLInputElement>) => {
        const value = (event.target as HTMLInputElement).checked;
        const tmp = { ...selectedData, [prop]: value };

        if (prop === 'ifEltif' && value) {
            tmp.openEnded = false;
        }

        // @ts-ignore
        const params = new URLSearchParams(
            // @ts-ignore
            Object.entries(tmp).map((entry) => {
                // @ts-ignore
                entry[1] = JSON.stringify(entry[1]);
                return entry;
            }),
        );

        setSearchParams(params.toString());
        setCheckClicked(true);

        setSelectedData(tmp);
    };

    const [onEditHandler, setOnEditHandler] = useState<() => void>(() => {
        return () => {
            setShowPopup(true);
            dispatchAsidePortalContentChanged(false);
        };
    });

    const handleEdit = () => {
        onEditHandler();
    };

    const setOnEdit = (handler: () => void) => {
        setOnEditHandler(() => handler);
    };

    const contextValue: LegalStructuresLayoutContextType = {
        // Selected data
        selectedData: selectedData,
        setSelectedData: setSelectedData,

        // Menu props
        handleCheckbox: handleCheckbox,
        active: true,
        hidden: false,
        onEdit: handleEdit,
        setOnEdit: setOnEdit,

        // Popup control
        showPopup: showPopup,
        setShowPopup: setShowPopup,

        // Page state
        pageReady: pageReady,
        setPageReady: setPageReady,
        tableUrl: tableUrl,
        setTableUrl: setTableUrl,

        // Route info
        isExpandedView: isExpandedView,

        checkClicked,
        setCheckClicked,
        toggleRequest,
        setToggleRequest,
    };

    // Show menu when we have data (either archDiagramData for main page or selectedData for comparison page)
    const shouldShowMenu =
        !showPopup && selectedData && selectedData.assets.length > 0 && selectedData.investors.length > 0;

    return (
        <LegalStructuresLayoutContext.Provider value={contextValue}>
            {shouldShowMenu &&
                document.querySelector('#menuAsideContent') &&
                createPortal(
                    <LegalStructureSelection
                        handleCheckbox={handleCheckbox}
                        selectedData={selectedData}
                        active={true}
                        hidden={false}
                        onEdit={handleEdit}
                    />,
                    document.querySelector('#menuAsideContent')!,
                )}

            <Outlet context={contextValue} />
        </LegalStructuresLayoutContext.Provider>
    );
};

export default LegalStructuresLayout;
